// MEDICATION ZONE — DYNAMIC, database-driven authority hub.
// Pulls every medication from /api/catalog/medications and groups by category.
// Tiles link to /best/:slug (the dynamic detail page).
// Owner can change pricing/copy/images/strengths from the Admin Catalog CMS
// and every change appears here instantly (no code, no rebuild).
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Scale, Heart, Sparkle, ArrowUpRight, Sparkles, ChevronRight,
} from "lucide-react";
import { goToShwcare } from "../../lib/shwcareHandoff";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const RED = "#E11D2A";

// Per-category meta (icon + tagline). The medications themselves come from the DB.
const CATEGORY_META = {
  "weight-loss":   { label: "Weight Loss",    Icon: Scale,   blurb: "GLP-1 protocols, metabolic boosters and microdosing — built around results." },
  "mens-health":   { label: "Men's Health",   Icon: Heart,   blurb: "Performance, hormones, hair and confidence — direct answers, no clichés." },
  "womens-health": { label: "Women's Health", Icon: Sparkle, blurb: "Hormones, hair, skin and wellness programs built specifically for women." },
};
const CATEGORY_ORDER = ["weight-loss", "mens-health", "womens-health"];

export default function MedicationZone() {
  const [meds, setMeds] = useState([]);
  // Pick-Your-Path state — null until the user chooses a category.
  // While null we render only the 3-tile picker; once chosen we render that
  // single category's medication grid. Initial value can come from the URL
  // hash so deep links like /#weight-loss work out of the box.
  const [selectedSlug, setSelectedSlug] = useState(() => {
    if (typeof window === "undefined") return null;
    const hash = (window.location.hash || "").replace(/^#/, "");
    return CATEGORY_META[hash] ? hash : null;
  });

  // Listen for in-app hash changes so deep links from <Link to="/#mens-health">
  // and browser back/forward both work without a full reload.
  useEffect(() => {
    const onHash = () => {
      const h = (window.location.hash || "").replace(/^#/, "");
      setSelectedSlug(CATEGORY_META[h] ? h : null);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    let live = true;
    fetch(`${API}/catalog/medications`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => { if (live) setMeds(data || []); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  const grouped = useMemo(() => {
    const map = {};
    for (const slug of CATEGORY_ORDER) map[slug] = [];
    for (const m of meds) {
      if (!m.active) continue;
      if (!map[m.category]) map[m.category] = [];
      map[m.category].push(m);
    }
    return CATEGORY_ORDER
      .filter((slug) => map[slug] && map[slug].length > 0)
      .map((slug) => ({ slug, ...CATEGORY_META[slug], items: map[slug] }));
  }, [meds]);

  return (
    <section
      id="medication-zone"
      data-testid="medication-zone-section"
      className="relative overflow-hidden bg-white text-black py-20 md:py-28"
    >
      {/* Ambient glass layers */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full opacity-[0.10]"
          style={{ background: `radial-gradient(closest-side, ${RED}, transparent 70%)` }} />
        <div className="absolute -bottom-40 -right-24 w-[520px] h-[520px] rounded-full opacity-[0.08]"
          style={{ background: "radial-gradient(closest-side, #000, transparent 70%)" }} />
        <div
          className="absolute inset-0 opacity-[0.045]"
          style={{
            backgroundImage:
              "linear-gradient(0deg, #000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            maskImage: "radial-gradient(circle at 50% 30%, black 0%, transparent 80%)",
          }}
        />
      </div>

      <div className="relative max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10">
        {/* PATH PICKER — default state when no category is selected. */}
        {!selectedSlug && (
          <PathPicker grouped={grouped} onPick={(slug) => {
            setSelectedSlug(slug);
            if (typeof window !== "undefined") {
              window.history.replaceState(null, "", `#${slug}`);
              // Scroll the picker out of view smoothly so the category grid lands flush.
              window.scrollTo({ top: document.getElementById("medication-zone")?.offsetTop || 0, behavior: "smooth" });
            }
          }} />
        )}

        {/* FOCUSED CATEGORY VIEW — fills the section once a path is chosen. */}
        {selectedSlug && (
          <FocusedCategoryView
            slug={selectedSlug}
            grouped={grouped}
            onSelect={(slug) => {
              setSelectedSlug(slug);
              if (typeof window !== "undefined") window.history.replaceState(null, "", `#${slug}`);
            }}
            onSwitch={() => {
              setSelectedSlug(null);
              if (typeof window !== "undefined") window.history.replaceState(null, "", "#");
            }}
          />
        )}
      </div>
    </section>
  );
}

/* ────────────────────────────  PATH PICKER  ────────────────────────────
   The dominant first-impression UX on the homepage. 3 large tiles — one per
   category — with the category's hero photo, item count and a primary CTA.
   Clicking a tile transitions the visitor into a focused single-category view. */
function PathPicker({ grouped, onPick }) {
  return (
    <>
      <header className="text-left max-w-[820px]" data-testid="path-picker">
        <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border text-[10.5px] font-extrabold tracking-[0.22em] uppercase"
          style={{ borderColor: RED, color: RED, background: "rgba(255,42,60,0.04)" }}
          data-testid="picker-eyebrow">
          <Sparkles size={11} /> Pick Your Path
        </span>
        <h2 className="font-heavy mt-4 tracking-[-0.02em] leading-[0.95] text-black text-5xl sm:text-6xl lg:text-7xl"
          data-testid="picker-title">
          PICK YOUR PATH.
        </h2>
        <p className="mt-5 text-xl sm:text-2xl font-extrabold leading-tight tracking-tight max-w-[640px]"
          data-testid="picker-tagline">
          One click. <span style={{ color: RED }}>Right medications appear.</span>
        </p>
        <p className="mt-4 text-[13.5px] sm:text-[14.5px] text-black/65 max-w-[560px]">
          No long scroll. No noise. Pick what you came for and the rest disappears.
        </p>
      </header>

      <div className="mt-10 lg:mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5">
        {grouped.length === 0 && (
          <p className="text-black/50 text-[13px] col-span-3" data-testid="picker-loading">Loading paths…</p>
        )}
        {grouped.map((cat) => (
          <PathTile key={cat.slug} cat={cat} onPick={() => onPick(cat.slug)} />
        ))}
      </div>
    </>
  );
}

function PathTile({ cat, onPick }) {
  const { Icon, label, blurb, items, slug } = cat;
  // Pick the first item's card_image as the visual anchor for this category.
  const heroSrc = items[0]?.card_image || items[0]?.image_url;
  return (
    <button
      type="button"
      onClick={onPick}
      data-testid={`path-tile-${slug}`}
      aria-label={`Explore ${label} medications`}
      className="group relative text-left rounded-2xl overflow-hidden bg-white transition-all duration-500 hover:-translate-y-1"
      style={{
        border: "1px solid rgba(0,0,0,0.08)",
        boxShadow: "0 28px 70px -40px rgba(0,0,0,0.30), 0 1px 0 rgba(255,255,255,0.6) inset",
      }}
    >
      <div aria-hidden className="absolute -top-px left-8 right-8 h-px opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ background: `linear-gradient(90deg, transparent, ${RED}, transparent)` }} />

      {/* Visual */}
      <div className="relative h-64 overflow-hidden"
        style={{ background: "linear-gradient(180deg, #FAFAFC 0%, #F4F6FA 60%, #FFFFFF 100%)" }}>
        <div aria-hidden className="absolute inset-0 opacity-60"
          style={{ background: `radial-gradient(120% 80% at 50% 0%, ${RED}1A, transparent 60%)` }} />
        {heroSrc && (
          <img src={heroSrc} alt={`${label} medications`}
            loading="lazy"
            className="absolute inset-0 m-auto h-full w-full object-contain transition-transform duration-700 group-hover:scale-[1.05]"
            draggable={false} />
        )}
        <span
          className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-extrabold tracking-[0.22em] uppercase bg-white/90 backdrop-blur"
          style={{ color: RED, border: `1px solid ${RED}33` }}
        >
          <Icon size={11} strokeWidth={2.4} /> {items.length} programs
        </span>
      </div>

      {/* Body */}
      <div className="px-5 pt-5 pb-6">
        <h3 className="font-heavy tracking-[-0.01em] leading-none text-black text-3xl">{label.toUpperCase()}</h3>
        <p className="mt-3 text-[13px] text-black/55 leading-relaxed line-clamp-2">{blurb}</p>
        <span
          className="mt-5 inline-flex items-center gap-1 px-4 py-2.5 text-[11px] font-extrabold tracking-[0.16em] uppercase text-white rounded-md transition-transform group-hover:translate-x-0.5"
          style={{ background: "#0A2342" }}
        >
          Explore {label} <ChevronRight size={12} />
        </span>
      </div>
    </button>
  );
}

/* ───────────────────────  FOCUSED CATEGORY VIEW  ───────────────────────
   Replaces the picker once the visitor selects a path. The tab strip at top
   lets them switch paths without leaving the homepage — exactly the
   "feels-like-a-new-section" UX requested. */
function FocusedCategoryView({ slug, grouped, onSelect, onSwitch }) {
  const cat = grouped.find((c) => c.slug === slug);
  if (!cat) {
    return (
      <p className="text-black/50 text-[13px]" data-testid="focused-empty">No medications in this path yet.</p>
    );
  }
  const { Icon, label, blurb, items } = cat;
  return (
    <section data-testid={`focused-${slug}`}>
      {/* Tab strip — quick switcher */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8 lg:mb-10">
        <button onClick={onSwitch}
          data-testid="focused-back"
          className="inline-flex items-center gap-1.5 text-[11px] font-extrabold tracking-[0.18em] uppercase text-black/55 hover:text-black">
          <ChevronRight size={12} className="rotate-180" /> Pick A Different Path
        </button>
        <div className="inline-flex rounded-full border bg-white p-1 gap-0.5"
          style={{ borderColor: "rgba(0,0,0,0.08)", boxShadow: "0 8px 20px -10px rgba(0,0,0,0.10)" }}>
          {grouped.map((c) => {
            const active = c.slug === slug;
            return (
              <button key={c.slug}
                onClick={() => onSelect(c.slug)}
                data-testid={`focused-tab-${c.slug}`}
                className="px-3 py-1.5 text-[10.5px] font-extrabold tracking-[0.18em] uppercase rounded-full transition-colors"
                style={active
                  ? { background: RED, color: "white" }
                  : { color: "rgba(0,0,0,0.55)" }}>
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Header for the chosen path */}
      <header className="mb-8 max-w-[720px]">
        <p className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] font-extrabold uppercase"
          style={{ color: RED }}
          data-testid="focused-eyebrow">
          <Icon size={12} /> {label} · {items.length} programs
        </p>
        <h2 className="mt-3 font-heavy tracking-[-0.02em] leading-[0.95] text-black text-5xl sm:text-6xl">
          {label.toUpperCase()}.
        </h2>
        <p className="mt-4 text-[14px] text-black/65 max-w-[560px]">{blurb}</p>
        <button
          type="button"
          onClick={() => goToShwcare(slug, { campaign: `bestrx_zone_${slug}` })}
          data-testid={`zone-shwcare-handoff-${slug}`}
          className="mt-5 inline-flex items-center gap-1.5 px-5 py-3 text-[11px] font-extrabold tracking-[0.16em] uppercase text-white rounded-md transition-transform hover:translate-x-0.5"
          style={{ background: RED, boxShadow: "0 10px 26px rgba(225,29,42,0.28)" }}
        >
          Continue with a licensed provider <ArrowUpRight size={13} strokeWidth={2.6} />
        </button>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
        {items.map((m) => (
          <MedicationTile key={m.slug} med={m} categorySlug={slug} categoryLabel={label} />
        ))}
      </div>
    </section>
  );
}

/* ────────────────────────────  MEDICATION TILE  ──────────────────────────── */
function MedicationTile({ med, categorySlug, categoryLabel }) {
  const price = `$${Math.round((med.starting_price_cents || 0) / 100)}`;
  const promoPrice = med.promo_price_cents
    ? `$${Math.round(med.promo_price_cents / 100)}`
    : null;
  return (
    <Link
      to={`/best/${med.slug}`}
      data-testid={`zone-tile-${categorySlug}-${med.slug}`}
      className="group relative block rounded-2xl overflow-hidden bg-white transition-all duration-500 hover:-translate-y-1"
      style={{
        border: "1px solid rgba(0,0,0,0.08)",
        boxShadow: "0 20px 60px -30px rgba(0,0,0,0.20), 0 1px 0 rgba(255,255,255,0.6) inset",
      }}
    >
      {/* Edge accent on hover */}
      <div aria-hidden className="absolute -top-px left-6 right-6 h-px transition-opacity opacity-0 group-hover:opacity-100"
        style={{ background: `linear-gradient(90deg, transparent, ${RED}, transparent)` }} />

      {/* Visual — photoreal product render */}
      <div className="relative h-56 overflow-hidden"
        style={{ background: "linear-gradient(180deg, #FAFAFC 0%, #F4F6FA 60%, #FFFFFF 100%)" }}>
        <div aria-hidden className="absolute inset-0 opacity-60"
          style={{ background: `radial-gradient(120% 80% at 50% 0%, ${(med.cap_color || RED)}1A, transparent 60%)` }} />
        <img
          src={med.card_image || med.image_url || `/assets/meds/${med.slug}.png`}
          alt={`${med.name} — ${categoryLabel}`}
          loading="lazy"
          className="absolute inset-0 m-auto h-full w-full object-contain transition-transform duration-700 group-hover:scale-[1.06]"
          draggable={false}
        />
        <span className="absolute top-3 left-3 z-10 text-[9.5px] tracking-[0.22em] font-extrabold uppercase px-2 py-0.5 rounded-full border bg-white/85 backdrop-blur"
          style={{ borderColor: "rgba(0,0,0,0.08)", color: "rgba(0,0,0,0.65)" }}>
          {categoryLabel}
        </span>
        {promoPrice && (
          <span className="absolute top-3 right-3 z-10 text-[9.5px] tracking-[0.22em] font-extrabold uppercase px-2 py-0.5 rounded-full text-white"
            style={{ background: RED }}>
            Promo
          </span>
        )}
      </div>

      {/* Content */}
      <div className="px-5 pt-4 pb-5">
        <h4 className="font-heavy tracking-tight text-black text-[19px] leading-tight">{med.name}</h4>
        <p className="mt-1.5 text-[12.5px] text-black/60 leading-relaxed min-h-[36px] line-clamp-2">
          {med.short_description}
        </p>

        {/* Price + CTA */}
        <div className="mt-4 flex items-end justify-between">
          <div>
            <p className="text-[9.5px] tracking-[0.18em] font-extrabold uppercase text-black/40">Starting at</p>
            <p className="font-heavy text-[20px] leading-none mt-0.5" style={{ color: RED }}>
              {promoPrice || price}
              <span className="text-black/45 text-[11px] font-extrabold ml-0.5">/mo</span>
            </p>
          </div>
          <span
            className="inline-flex items-center gap-1 px-3 py-2 text-[10.5px] font-extrabold tracking-[0.14em] uppercase rounded-md text-white transition-transform group-hover:translate-x-0.5"
            style={{ background: "#0A2342" }}
          >
            Learn More <ChevronRight size={11} />
          </span>
        </div>
      </div>
    </Link>
  );
}
